import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CarCard } from '@perxis-auto/ui-kit/lib/components/CarCard';

import BenefitsTooltip from '@/components/BenefitsTooltip';
import useBenefitsChoicePrice from '@/features/benefitsChoice/hooks/useBenefitsChoicePrice';
import getCarBenefits from '@/features/cars/helpers/getCarBenefits';
import { useAppSelector } from '@/features/hooks';
import { rootSelectors } from '@/features/store';
import formatPrice from '@/utils/formatPrice';

import { CardPriceProps } from './CardPrice.types';

/** Блок цены в карточке автомобиля */
function CardPrice({
  data,
  label,
  value: customPrice,
  oldValue: customOldPrice,
  oldValuePosition,
  prefix,
  postfix,
  isLoading = false,
}: CardPriceProps) {
  const carId = data?.id;
  const { price: defaultPrice, minPrice } = data?.data || {};

  const benefits = getCarBenefits(data);

  const { t } = useTranslation();

  const { enable, showOnlyBlock } = useAppSelector(rootSelectors.config.benefitsSelector);

  const isBenefitsEnabled = enable && !showOnlyBlock;

  // Цена автомобиля (с учетом выгод, если они есть)
  const { price, oldPrice } = useBenefitsChoicePrice(
    {
      price: defaultPrice,
      minPrice,
      carId,
    },
    {
      isBenefitsEnabled,
    },
  );

  // Если  передана кастомная цена, то используется она
  const resolvedPrice = customPrice || price;
  const resolvedOldPrice = customOldPrice || oldPrice;

  const formattedPrice = useMemo(
    () => formatPrice(resolvedPrice, prefix, postfix, t),
    [resolvedPrice, prefix, postfix, t],
  );
  const formattedOldPrice = useMemo(
    () => formatPrice(resolvedOldPrice, prefix, postfix, t),
    [resolvedOldPrice, prefix, postfix, t],
  );

  // Тултип с выгодами отображается, если они включены в настройках и имеются у конкретного автомобиля
  const isDisplayBenefitsTooltip = isBenefitsEnabled && benefits && benefits.length > 0;
  // Старая цена отображается, если выгоды включены и она не равна цене самого автомобиля.
  const isDisplayOldPrice = isBenefitsEnabled && resolvedPrice !== resolvedOldPrice;

  // Возвращает разметку для тултипа выгод
  const tooltipContent = useMemo(() => {
    if (!benefits || !resolvedOldPrice) {
      return undefined;
    }

    return <BenefitsTooltip benefits={benefits} carPrice={resolvedOldPrice} />;
  }, [benefits, resolvedOldPrice]);

  if (isLoading) {
    return <CarCard.PriceSkeleton />;
  }

  if (!data || !price) {
    return null;
  }

  return (
    <CarCard.Price
      price={{
        label: label ?? t('card.price'),
        currentValue: formattedPrice,
        oldValue: isDisplayOldPrice ? formattedOldPrice : undefined,
        oldValuePosition,
      }}
      benefits={{
        hidden: !isDisplayBenefitsTooltip,
        tooltipContent,
      }}
    />
  );
}

export default CardPrice;
